import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons/faPaperclip';
import { faPaperclipVertical } from '@fortawesome/pro-regular-svg-icons/faPaperclipVertical';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { colorPalette } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { IconButton } from '../buttons';
import styles from './attachments-badge.module.scss';

type AttachmentsBadgeProps = {
  count: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
  /** 'chip' is the Figma design (knowledge cards); 'plain' keeps the original communications look */
  variant?: 'chip' | 'plain';
};

export function AttachmentsBadge({
  count,
  onClick,
  className,
  variant = 'chip',
}: AttachmentsBadgeProps) {
  const lang = useSelector(getLang('COMMUNICATION_CENTER'));

  if (!count) {
    return null;
  }

  const ariaLabel = (
    count === 1 ? lang.SINGLE_ATTACHMENT_ARIA : lang.MULTIPLE_ATTACHMENTS_ARIA
  )?.replace('{count}', String(count));

  const rootClassName = classnames(
    styles.badge,
    variant === 'chip' && styles.chip,
    variant === 'plain' && styles.plain,
    className,
  );

  const content =
    variant === 'chip' ? (
      <>
        <FontAwesomeIcon icon={faPaperclip} className={styles.chipIcon} />
        <label aria-hidden="true">{formatNumber(count)}</label>
      </>
    ) : (
      <>
        <label aria-hidden="true">{formatNumber(count)}</label>
        <FontAwesomeIcon icon={faPaperclipVertical} color={colorPalette.neutral6} />
      </>
    );

  // No action → no button: a focusable control that does nothing confuses keyboard users
  if (!onClick) {
    return (
      <span role="img" aria-label={ariaLabel} className={rootClassName}>
        {content}
      </span>
    );
  }

  return (
    <IconButton onClick={onClick} className={rootClassName} label={ariaLabel}>
      {content}
    </IconButton>
  );
}
